import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * Toast (เดสก์ท็อป) — ต่างจากแอปมือถือสองข้อ: ซ้อนได้หลายข้อความ และมีปุ่ม "เลิกทำ"
 *
 * เหตุผลของ "เลิกทำ": เมื่อทำงานด้วยคีย์ลัด การกด A ผิดรายการเป็นความผิดพลาด
 * ที่เกิดบ่อยที่สุด ถ้าไม่มีทางย้อน เจ้าหน้าที่จะเลิกใช้คีย์ลัดและงานกลับไปช้าเท่าเดิม
 * (ฝั่ง API ต้องมี endpoint ยกเลิกการอนุมัติภายในหน้าต่างเวลาสั้น ๆ — อยู่ใน ADMIN-API-REQUIREMENTS)
 */
export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  message: string;
  /** ปุ่มเลิกทำ — แสดงพร้อมเวลานับถอยหลัง */
  undo?: { label?: string; onUndo: () => void; seconds?: number };
  /** ms ก่อนหายไปเอง — 0 = ไม่หายเอง */
  duration?: number;
}

const TONE: Record<ToastTone, { icon: string; bg: string; text: string; border: string }> = {
  success: { icon: 'check_circle', bg: 'bg-[var(--success-bg)]', text: 'text-[var(--success-text)]', border: 'border-[var(--success-border)]' },
  error: { icon: 'error', bg: 'bg-[var(--error-bg)]', text: 'text-[var(--error-text)]', border: 'border-[var(--error-border)]' },
  info: { icon: 'info', bg: 'bg-[var(--info-bg)]', text: 'text-[var(--info-text)]', border: 'border-[var(--info-border)]' },
  warning: { icon: 'warning', bg: 'bg-[var(--warning-bg)]', text: 'text-[var(--warning-text)]', border: 'border-[var(--warning-border)]' },
};

interface ToastApi {
  show: (item: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast ต้องอยู่ใน <ToastProvider>');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode; max?: number }> = ({ children, max = 4 }) => {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Record<string, number>>({});

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) { window.clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const show = useCallback((item: Omit<ToastItem, 'id'>) => {
    const id = `t${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
    const duration = item.duration ?? (item.undo ? (item.undo.seconds ?? 8) * 1000 : 4000);
    setItems((prev) => [...prev.slice(-(max - 1)), { ...item, id }]);
    if (duration > 0) timers.current[id] = window.setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss, max]);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-[var(--space-5)] right-[var(--space-5)] z-[var(--z-toast)] flex w-[380px] max-w-[92vw] flex-col gap-[var(--space-2)]"
      >
        {items.map((t) => {
          const tone = TONE[t.tone];
          return (
            <div
              key={t.id}
              role={t.tone === 'error' ? 'alert' : 'status'}
              className={cn(
                'pointer-events-auto flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border p-[var(--space-3)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-toast)]',
                tone.bg, tone.border,
              )}
            >
              <Icon name={tone.icon} size={20} filled className={tone.text} />
              <p className={cn('flex-1 text-[length:var(--text-form)] leading-[var(--leading-thai)]', tone.text)}>{t.message}</p>
              {t.undo && (
                <button
                  type="button"
                  onClick={() => { t.undo!.onUndo(); dismiss(t.id); }}
                  className={cn(
                    'min-h-[var(--hit-table)] shrink-0 rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1',
                    tone.text,
                  )}
                >
                  {t.undo.label ?? 'เลิกทำ'}
                </button>
              )}
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="ปิดข้อความนี้"
                className={cn('shrink-0 rounded-[var(--radius-sm)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]', tone.text)}
              >
                <Icon name="close" size={18} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
